/**
 * Pure utility functions for general chat context hashing.
 *
 * The general chat sidebar keeps the backend agent aligned with current node config
 * by sending `set_context` WebSocket messages. We fingerprint the payload and only
 * send when the digest changes — so the agent's context stays up to date without
 * spamming the socket.
 */

/** Walks nested objects; sorts keys so two equivalent configs produce the same string for hashing. */
export function generalChatContextStableStringify(value: unknown): string {
  if (value === null) return 'null';
  const t = typeof value;
  if (t === 'undefined') return 'undefined';
  if (t !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((v) => generalChatContextStableStringify(v)).join(',')}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort();
  return `{${keys
    .map(
      (k) =>
        `${JSON.stringify(k)}:${generalChatContextStableStringify(obj[k])}`,
    )
    .join(',')}}`;
}

/**
 * Short FNV-1a-style digest of the `set_context` body. Compared to the last value
 * we sent for a given node; if equal, node config is unchanged and we skip another
 * `set_context` before `chat_message`.
 */
export function hashGeneralChatSetContextPayload(
  payload: Record<string, unknown>,
): string {
  const stable = generalChatContextStableStringify(payload);
  let h = 0x811c9dc5;
  for (let i = 0; i < stable.length; i++) {
    h ^= stable.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}
